import { convertDateFromSourceToUserTimeZone, parseDate, parseISOString } from '@/core/lib/date';
import { formatStream } from '@/core/oddsApi/helpers/formatEvent';
import { formatTranslation } from '@/core/oddsApi/helpers/formatTranslation';
import type { useStreamEventsQuery } from '@/core/oddsApi/oddsApiType';
import { EnumHandicapType } from '@/core/oddsApi/oddsApiType';
import type { IJersey } from '@/interface/IJersey';
import type { IStreamEvent } from '@/interface/IStream';
import { LeagueSize, SportType, TimezoneOffset } from '@/interface/enum';

export const formatJersey = (jersey: string): IJersey => {
    const [type, leftSleeve, rightSleeve, body, stripe] = jersey.split(';');
    return {
        type: type ? parseInt(type) : 0,
        leftSleeve: leftSleeve ? `#${leftSleeve}` : null,
        rightSleeve: rightSleeve ? `#${rightSleeve}` : null,
        body: body ? `#${body}` : null,
        stripe: stripe ? `#${stripe}` : null,
    };
};

export const formatStreamEvent = (event: NonNullable<(ReturnType<typeof useStreamEventsQuery>)['result']['value']>[number]): IStreamEvent => {
    const handicapType = event.isLive
        ? event.mainMarketEventResult.liveHandicapType
        : event.mainMarketEventResult.nonLiveHandicapType;
    return {
        eventId: event.id,
        eventCode: event.eventCode,
        isLive: event.isLive,
        isSimulation: event.isSimulation,
        sportType: SportType.fromOddsApi(event.mainMarketEventResult.sport),
        realKickoffTime: convertDateFromSourceToUserTimeZone(
            parseISOString(event.realKickoffTime),
            TimezoneOffset.Server,
        ),
        showTime: parseDate(event.showTime, 'MM/dd/yyyy HH:mm', TimezoneOffset.Singapore),
        showTimeType: event.showTimeType,
        homeTeam: {
            name: event.homeTeam?.teamName?.map(x => formatTranslation(x)) ?? [],
            jersey: formatJersey(event.homeJersey),
            redCard: event.mainMarketEventResult.extraInfo?.homeRedCards ?? 0,
            liveScore: event.mainMarketEventResult.liveHomeScore,
            isFavorite: handicapType === EnumHandicapType.Home,
        },
        awayTeam: {
            name: event.awayTeam?.teamName?.map(x => formatTranslation(x)) ?? [],
            jersey: formatJersey(event.awayJersey),
            redCard: event.mainMarketEventResult.extraInfo?.awayRedCards ?? 0,
            liveScore: event.mainMarketEventResult.liveAwayScore,
            isFavorite: handicapType === EnumHandicapType.Away,
        },
        extraInfo: {
            round: event.mainMarketEventResult.extraInfo?.round ?? 0,
            injuryTime: event.mainMarketEventResult.extraInfo?.injuryTime ?? 0,
            period: event.mainMarketEventResult.extraInfo?.period ?? 0,
            periodStartTime: event.mainMarketEventResult.extraInfo?.periodStartTime ?? '0001-01-01T00:00:00',
        },
        league: {
            id: event.tournament.id,
            name: event.tournament.tournamentName.map(x => formatTranslation(x)),
            size: LeagueSize.fromOddsApi(event.tournament.tournamentSize),
        },
        stream: formatStream(event),
    };
};
